import { GetServerSideProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Head from "next/head";
import { FC, Fragment } from "react";
import Experience from "~/components/HomeSections/Experience";
import Layout from "~/components/Layout";
import ResourceBlogDetails from "~/components/ResourceSections/ResourceBlogDetails";
import ResourceVideoDetails from "~/components/ResourceSections/ResourceVideoDetails";
import Api from "~/services/api";
import { Resource, ResourceTypes } from "~/services/resource";

interface ResourcesDetailsProps {
  resource: Resource;
  type: ResourceTypes;
}

const ResourcesDetails: FC<ResourcesDetailsProps> = ({ resource, type }) => {
  const { t } = useTranslation();
  const pageThumbnail =
    resource.thumbnail?.url || "https://cxgenie.ai/sharing-image.png";
  const pageDescription =
    resource.short_description ||
    "CX Genie is your Virtual Agent that merges AI & Your Support Team to deliver faster, more consistent and tailored customer experience.";
  return (
    <Layout>
      <Head>
        <title key="title">{resource.name}</title>
        <meta key="mt:title" name="title" content={resource.name} />
        <meta key="description" name="description" content={pageDescription} />

        <meta key="og:title" property="og:title" content={resource.name} />
        <meta
          key="og:description"
          name="og:description"
          content={pageDescription}
        />
        <meta key="og:image" property="og:image" content={pageThumbnail} />
        <meta property="og:image:alt" content={pageDescription} />

        <meta
          key="twitter:title"
          name="twitter:title"
          content={resource.name}
        />
        <meta
          key="twitter:description"
          name="twitter:description"
          content={pageDescription}
        />
        <meta
          key="twitter:image"
          name="twitter:image"
          content={pageThumbnail}
        />
      </Head>

      <div className="resources mb-[100px]">
        {type === ResourceTypes.PRODUCT_VIDEOS ? (
          <ResourceVideoDetails resource={resource} />
        ) : (
          <ResourceBlogDetails resource={resource} type={type} />
        )}
      </div>

      <Experience />
    </Layout>
  );
};

export default ResourcesDetails;

export const getServerSideProps: GetServerSideProps = async ({
  params: _params,
  locale,
}) => {
  const type = _params?.type;
  const params = {
    page: 1,
    page_size: 1,
    filters: {
      slug: _params?.slug,
    },
    populate: ["thumbnail", "related_blogs.thumbnail", "categories"],
  };

  let resource: Resource | undefined = undefined;

  switch (type) {
    case ResourceTypes.PRODUCT_VIDEOS: {
      const res = await Api<{ data: any }>({
        url: "/v1/resources/product-videos",
        method: "GET",
        params,
      });

      resource = res?.data?.[0];
      break;
    }
    case ResourceTypes.OLD_BLOGS:
    case ResourceTypes.BLOGS: {
      const res = await Api<{ data: any }>({
        url: "/v1/resources/guides",
        method: "GET",
        params,
      });

      resource = res?.data?.[0];
      break;
    }

    case ResourceTypes.CASE_STUDY: {
      const res = await Api<{ data: any }>({
        url: "/v1/resources/case-studies",
        method: "GET",
        params,
      });

      resource = res?.data?.[0];
      break;
    }

    case ResourceTypes.RELEASES: {
      const res = await Api<{ data: any }>({
        url: "/v1/resources/releases",
        method: "GET",
        params,
      });

      resource = res?.data?.[0];
      break;
    }
  }

  return {
    props: {
      resource,
      type,
      ...(await serverSideTranslations(locale || "en", ["common"])),
    },
  };
};
